import { AxiosRequestConfig } from 'axios';
import { CaseType, DocumentObject, deserialize } from 'jsonapi-fractal';
import instance from '../axiosInstance';

/**
 * Helpers for consuming JSON:API endpoints.
 *
 * Resources are flattened to `{ id, ...attributes }` (relationships and
 * `included` are resolved by jsonapi-fractal). By default attribute names are
 * left exactly as the API sends them (snake_case), so an endpoint can be moved
 * to JSON:API without touching the interfaces or components that read it.
 */

export type JsonApiPagination = {
  page: number;
  pages: number;
  count: number;
};

export type JsonApiMeta = {
  pagination?: JsonApiPagination;
  [key: string]: unknown;
};

export type JsonApiLinks = {
  first?: string | null;
  last?: string | null;
  next?: string | null;
  prev?: string | null;
  [key: string]: unknown;
};

export type JsonApiOptions = {
  /**
   * `'preserve'` (default) keeps attribute names as sent by the API;
   * `'camel'` converts them to camelCase.
   */
  keyCase?: 'preserve' | 'camel';
  /**
   * JSON:API ids are always strings. Set this to convert numeric ids on the
   * top-level resources to numbers, for interfaces that still type `id` as a
   * number.
   */
  numericIds?: boolean;
};

export type JsonApiResult<T> = {
  data: T;
  meta: JsonApiMeta;
  links: JsonApiLinks;
};

/** jsonapi-fractal's `DocumentObject` omits the top-level `links` member. */
export type JsonApiDocument = DocumentObject & { links?: JsonApiLinks };

type RequestOptions = JsonApiOptions & {
  params?: AxiosRequestConfig['params'];
};

const toNumericId = <T>(resource: T): T => {
  if (!resource || typeof resource !== 'object') return resource;
  const { id } = resource as { id?: unknown };
  if (typeof id === 'string' && id.trim() !== '' && !Number.isNaN(Number(id))) {
    return { ...resource, id: Number(id) };
  }
  return resource;
};

/**
 * Flattens a JSON:API document into plain objects. For a collection `T`
 * should be the array type, e.g. `parseJsonApi<Tag[]>(doc)`.
 */
export function parseJsonApi<T>(
  document: JsonApiDocument,
  { keyCase = 'preserve', numericIds = false }: JsonApiOptions = {},
): JsonApiResult<T> {
  let data = deserialize(
    document,
    keyCase === 'camel' ? { changeCase: CaseType.camelCase } : {},
  );

  if (numericIds) {
    data = Array.isArray(data) ? data.map(toNumericId) : toNumericId(data);
  }

  return {
    data: data as T,
    meta: (document.meta ?? {}) as JsonApiMeta,
    links: document.links ?? {},
  };
}

/** GETs a JSON:API endpoint and returns the flattened document. */
export async function getJsonApi<T>(
  url: string,
  { params, ...options }: RequestOptions = {},
): Promise<JsonApiResult<T>> {
  const response = await instance.get<JsonApiDocument>(url, { params });
  return parseJsonApi<T>(response.data, options);
}

/**
 * GETs every page of a JSON:API collection and returns all resources in
 * order. The first page is fetched to learn the page count from
 * `meta.pagination.pages`; any remaining pages are fetched in parallel.
 */
export async function getAllJsonApiPages<T>(
  url: string,
  { params, ...options }: RequestOptions = {},
): Promise<T[]> {
  const pageParams = (page: number) => ({ ...params, 'page[number]': page });

  const first = await getJsonApi<T[]>(url, {
    ...options,
    params: pageParams(1),
  });
  const pages = first.meta.pagination?.pages ?? 1;

  const rest = await Promise.all(
    Array.from({ length: Math.max(pages - 1, 0) }, (_, i) =>
      getJsonApi<T[]>(url, { ...options, params: pageParams(i + 2) }),
    ),
  );

  return [first, ...rest].flatMap(({ data }) => data ?? []);
}
